import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowLeft, 
  ChevronRight, 
  ChevronDown,
  FileText
} from 'lucide-react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { dashboardApi } from '../../services/dashboardApi';
import CashbookReportView from './CashbookReportView';

const CashbookReports = ({ onClose, onDayClick, initialEntries, initialSummary }) => {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [showPrintView, setShowPrintView] = useState(false);
  const [selectedRange, setSelectedRange] = useState('all');
  
  const now = new Date();
  const formatDate = (date) => {
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();
    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return [year, month, day].join('-');
  };

  const [fromDate, setFromDate] = useState('2020-01-01');
  const [toDate, setToDate] = useState(formatDate(now));

  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  const generateReport = (entries, start, end) => {
    let flatEntries = [];
    if (entries && typeof entries === 'object' && !Array.isArray(entries)) {
      Object.values(entries).forEach(dayEntries => {
        if (Array.isArray(dayEntries)) {
          flatEntries = [...flatEntries, ...dayEntries];
        }
      });
    } else if (Array.isArray(entries)) {
      flatEntries = entries;
    } else {
      return;
    }

    const from = new Date(start);
    const to = new Date(end);
    to.setHours(23, 59, 59);

    const filtered = flatEntries.filter(e => {
      const d = new Date(e.entry_date || e.date);
      return d >= from && d <= to;
    });

    let totalIn = 0;
    let totalOut = 0;
    const daysMap = {};

    filtered.forEach(e => {
      const amt = parseFloat(e.amount) || 0;
      const type = e.entry_type || e.type;
      const rawDate = (e.entry_date || e.date || "").split(' ')[0];
      if (!rawDate) return;

      if (type === 'in') totalIn += amt;
      else totalOut += amt;

      if (!daysMap[rawDate]) daysMap[rawDate] = { date: rawDate, balance: 0, total_in: 0, total_out: 0, entries: [] };
      daysMap[rawDate].entries.push(e);
      if (type === 'in') {
        daysMap[rawDate].total_in += amt;
        daysMap[rawDate].balance += amt;
      } else {
        daysMap[rawDate].total_out += amt;
        daysMap[rawDate].balance -= amt;
      }
    });

    const daysList = Object.values(daysMap).sort((a, b) => new Date(b.date) - new Date(a.date));

    setReportData({
      total_in: totalIn,
      total_out: totalOut,
      closing_balance: totalIn - totalOut,
      daily_summaries: daysList,
      entries: filtered
    });
  };

  useEffect(() => {
    if (initialEntries && initialEntries.length > 0) {
      generateReport(initialEntries, fromDate, toDate);
    } else {
      fetchFallbackData();
    }
  }, [fromDate, toDate, initialEntries]);

  const fetchFallbackData = async () => {
    setLoading(true);
    try {
      const res = await dashboardApi.getCashbookData();
      if (res?.success) {
        const entries = res.data?.entries || [];
        generateReport(entries, fromDate, toDate);
      }
    } catch (e) {
      console.error("Fallback error:", e);
    } finally { setLoading(false); }
  };

  const applyDateRange = (type) => {
    let from = fromDate;
    let to = formatDate(now);
    switch (type) {
      case 'today': from = to; break;
      case 'last_week': 
        const lw = new Date(); lw.setDate(now.getDate() - 7); from = formatDate(lw); break;
      case 'this_month': from = formatDate(new Date(now.getFullYear(), now.getMonth(), 1)); break;
      case 'last_month': 
        from = formatDate(new Date(now.getFullYear(), now.getMonth() - 1, 1));
        to = formatDate(new Date(now.getFullYear(), now.getMonth(), 0)); break;
      case 'all': from = '2020-01-01'; break;
      default: break;
    }
    setFromDate(from);
    setToDate(to);
    setSelectedRange(type);
  };

  if (showPrintView && reportData) {
    return <CashbookReportView reportData={reportData} fromDate={fromDate} toDate={toDate} onBack={() => setShowPrintView(false)} />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.appBar}>
        <button style={styles.backBtn} onClick={onClose}><ArrowLeft size={22} color="#fff" /></button>
        <h2 style={styles.appBarTitle}>{t(LangKeys.cashbook_report_title)}</h2>
      </div>

      <div style={styles.scrollArea}>
        <div style={styles.filterCard}>
          <div style={styles.dateChipRow}>
            <div style={styles.dateChip} onClick={() => fromDateRef.current?.showPicker()}>
              <input ref={fromDateRef} type="date" style={styles.hiddenInput} value={fromDate} onChange={(e) => { setFromDate(e.target.value); setSelectedRange('custom'); }} />
              <span style={styles.dateText}>{fromDate.split('-').reverse().join('-')}</span>
            </div>
            <span style={styles.toSeparator}>{t(LangKeys.to)}</span>
            <div style={styles.dateChip} onClick={() => toDateRef.current?.showPicker()}>
              <input ref={toDateRef} type="date" style={styles.hiddenInput} value={toDate} onChange={(e) => { setToDate(e.target.value); setSelectedRange('custom'); }} />
              <span style={styles.dateText}>{toDate.split('-').reverse().join('-')}</span>
            </div>
          </div>
          <div style={styles.dropdownWrap}>
            <select style={styles.select} value={selectedRange} onChange={(e) => applyDateRange(e.target.value)}>
              <option value="custom">{t(LangKeys.date_range)}</option>
              <option value="today">{t(LangKeys.single_day)}</option>
              <option value="this_month">{t(LangKeys.this_month)}</option>
              <option value="last_week">{t(LangKeys.last_week)}</option>
              <option value="last_month">{t(LangKeys.last_month)}</option>
              <option value="all">{t(LangKeys.all)}</option>
            </select>
            <ChevronDown size={18} style={styles.selectIcon} color="#6B7280" />
          </div>
        </div>

        <div style={styles.summaryCard}>
          <div style={styles.summaryRow}>
            <span style={styles.sumLabel}>{t(LangKeys.in_entry)}</span>
            <span style={{...styles.sumVal, color: '#16A34A'}}>₹ {(reportData?.total_in || 0).toLocaleString()}</span>
          </div>
          <div style={styles.summaryRow}>
            <span style={styles.sumLabel}>{t(LangKeys.out_entry)}</span>
            <span style={{...styles.sumVal, color: '#DC2626'}}>₹ {(reportData?.total_out || 0).toLocaleString()}</span>
          </div>
          <div style={styles.divider} />
          <div style={styles.summaryRow}>
            <span style={{...styles.sumLabel, fontWeight: '700'}}>{t(LangKeys.closing_balance)}</span>
            <span style={{...styles.sumVal, color: (reportData?.closing_balance >= 0 ? '#16A34A' : '#DC2626')}}>
              ₹ {(reportData?.closing_balance || 0).toLocaleString()}
            </span>
          </div>
        </div>

        <div style={styles.listContainer}>
          {loading ? (
            <div style={styles.statusMsg}>{t(LangKeys.loading)}</div>
          ) : reportData?.daily_summaries?.length > 0 ? (
            reportData.daily_summaries.map((day, idx) => (
              <div 
                key={idx} 
                style={styles.dayCard} 
                onClick={() => {
                  if (typeof onDayClick === 'function') {
                    onDayClick(day.date);
                  }
                }}
              >
                <div style={styles.dayInfo}>
                  <span style={styles.dayDate}>{day.date.split('-').reverse().join('-')}</span>
                  <span style={styles.daySub}>{t(LangKeys.daily_balance_short)}</span>
                </div>
                <div style={styles.dayRight}>
                  <div style={styles.dayAmts}>
                    <span style={{...styles.dayMainAmt, color: (day.balance < 0 ? '#DC2626' : '#16A34A')}}>
                      ₹ {day.balance?.toLocaleString()}
                    </span>
                    <span style={styles.dayTotalAmt}>{t(LangKeys.total)} ₹ {day.balance?.toLocaleString()}</span>
                  </div>
                  <ChevronRight size={18} color="#9CA3AF" />
                </div>
              </div>
            ))
          ) : (
            <div style={styles.statusMsg}>{t(LangKeys.no_entries_found_period)}</div>
          )}
        </div>
      </div>

      <div style={styles.footer}>
        <button style={styles.downloadBtn} onClick={() => setShowPrintView(true)} disabled={loading || !reportData}>
          <FileText size={20} />
          <span>{t(LangKeys.download_pdf).toUpperCase()}</span>
        </button>
      </div>
    </div>
  );
};

const styles = {
  container: { height: '100%', display: 'flex', flexDirection: 'column', background: '#F3F4F6', position: 'relative', overflow: 'hidden', borderRadius: '16px', padding: '16px' },
  appBar: { background: '#1D4ED8', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '16px', borderRadius: '14px 14px 0 0' },
  appBarTitle: { color: '#FFFFFF', fontSize: '18px', fontWeight: '700', margin: 0 },
  backBtn: { background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex' },
  scrollArea: { flex: 1, overflowY: 'auto', paddingBottom: '100px' },
  filterCard: { margin: '14px', padding: '16px', background: '#FFFFFF', borderRadius: '18px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' },
  dateChipRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' },
  dateChip: { flex: 1, position: 'relative', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '10px', textAlign: 'center' },
  hiddenInput: { position: 'absolute', top: 0, left: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer' },
  dateText: { fontSize: '14px', fontWeight: '600', color: '#111827' },
  toSeparator: { fontSize: '13px', fontWeight: '700', color: '#6B7280' },
  dropdownWrap: { position: 'relative', background: '#F9FAFB', borderRadius: '12px', border: '1px solid #E5E7EB' },
  select: { width: '100%', padding: '10px 14px', background: 'transparent', border: 'none', fontSize: '14px', fontWeight: '600', color: '#111827', appearance: 'none', outline: 'none', cursor: 'pointer' },
  selectIcon: { position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' },
  summaryCard: { margin: '0 14px 14px 14px', padding: '12px 18px', background: '#FFFFFF', borderRadius: '18px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' },
  summaryRow: { display: 'flex', justifyContent: 'space-between', padding: '12px 0', alignItems: 'center' },
  sumLabel: { fontSize: '15px', color: '#6B7280', fontWeight: '600' },
  sumVal: { fontSize: '17px', fontWeight: '800' },
  divider: { height: '1px', background: '#F1F5F9', margin: '2px 0' },
  listContainer: { padding: '0 14px' },
  dayCard: { background: '#FFFFFF', borderRadius: '14px', padding: '14px 16px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', cursor: 'pointer' },
  dayInfo: { display: 'flex', flexDirection: 'column', gap: '2px' },
  dayDate: { fontSize: '15px', fontWeight: '700', color: '#374151' },
  daySub: { fontSize: '12px', color: '#9CA3AF' },
  dayRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  dayAmts: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end' },
  dayMainAmt: { fontSize: '15px', fontWeight: '800' },
  dayTotalAmt: { fontSize: '11px', color: '#9CA3AF' },
  footer: { position: 'absolute', bottom: 0, left: 0, right: 0, padding: '16px', background: '#FFFFFF', borderTop: '1px solid #E5E7EB', zIndex: 20 },
  downloadBtn: { width: '100%', height: '52px', background: '#1D4ED8', color: '#FFFFFF', border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' },
  statusMsg: { padding: '40px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' }
};

export default CashbookReports;
